import React, { useState, useRef, useEffect } from 'react';
import Picker from 'react-mobile-picker';
import { useDesign } from '../../designs';
import { Clock } from 'lucide-react';
import { formatTime12h } from './TimeSpinner';

interface ActivityTimePickerProps {
  label: string;
  value: string; // "HH:MM" 24-hour
  onChange: (value: string) => void;
}

const HOURS = Array.from({ length: 12 }, (_, i) => (i + 1).toString());
const MINUTES = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));
const AMPM = ['AM', 'PM'];

function parse24(time24: string): { hour: string; minute: string; ampm: string } {
  if (!time24) return { hour: '12', minute: '00', ampm: 'PM' };
  const [hStr, mStr] = time24.split(':');
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr || '0', 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  // Snap minute to nearest 5
  const snapped = Math.round(m / 5) * 5 % 60;
  return { hour: h.toString(), minute: snapped.toString().padStart(2, '0'), ampm };
}

function combine(hour: string, minute: string, ampm: string): string {
  const h12 = parseInt(hour, 10);
  const m = parseInt(minute, 10);
  let h24 = h12 % 12;
  if (ampm === 'PM') h24 += 12;
  return `${h24.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}

export default function ActivityTimePicker({ label, value, onChange }: ActivityTimePickerProps) {
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(parse24(value));
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDraft(parse24(value));
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const displayValue = value ? formatTime12h(value) : '—';

  const handleConfirm = () => {
    onChange(combine(draft.hour, draft.minute, draft.ampm));
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setOpen(false);
  };

  return (
    <div ref={containerRef} style={{ flex: 1, minWidth: 0, position: 'relative' }}>
      <label style={{
        display: 'block', fontFamily: "'Raleway', sans-serif",
        fontSize: fs.sectionHeader, fontWeight: 700, letterSpacing: '1.5px',
        textTransform: 'uppercase', color: d.textSecondary, marginBottom: '6px',
      }}>{label}</label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', padding: '10px 12px', minHeight: '41px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyInput, fontWeight: 400,
          color: value ? d.textPrimary : d.textSecondary,
          backgroundColor: d.bgSurface,
          border: `1px solid ${open ? 'rgba(200,146,58,0.30)' : 'rgba(200,146,58,0.10)'}`,
          borderRadius: '2px', outline: 'none', boxSizing: 'border-box',
          cursor: 'pointer', textAlign: 'left',
          WebkitAppearance: 'none', appearance: 'none',
        }}
      >
        <span>{displayValue}</span>
        <Clock size={14} strokeWidth={1.5} color={d.textSecondary} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 100,
          backgroundColor: d.bgSurface,
          border: `1px solid rgba(200,146,58,0.30)`,
          borderRadius: '2px',
          padding: '12px 8px 8px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
        }}>
          <Picker
            value={{ hour: draft.hour, minute: draft.minute, ampm: draft.ampm }}
            onChange={(v) => setDraft({ hour: v.hour, minute: v.minute, ampm: v.ampm })}
            height={140}
            itemHeight={32}
            wheelMode="normal"
          >
            <Picker.Column name="hour">
              {HOURS.map((h) => (
                <Picker.Item key={h} value={h}>
                  {({ selected }) => (
                    <span style={{
                      fontFamily: "'Raleway', sans-serif",
                      fontSize: selected ? fs.bodyLarge : fs.bodyInput,
                      fontWeight: selected ? 700 : 400,
                      color: selected ? d.accent : d.textSecondary,
                    }}>{h}</span>
                  )}
                </Picker.Item>
              ))}
            </Picker.Column>
            <Picker.Column name="minute">
              {MINUTES.map((m) => (
                <Picker.Item key={m} value={m}>
                  {({ selected }) => (
                    <span style={{
                      fontFamily: "'Raleway', sans-serif",
                      fontSize: selected ? fs.bodyLarge : fs.bodyInput,
                      fontWeight: selected ? 700 : 400,
                      color: selected ? d.accent : d.textSecondary,
                    }}>{m}</span>
                  )}
                </Picker.Item>
              ))}
            </Picker.Column>
            <Picker.Column name="ampm">
              {AMPM.map((ap) => (
                <Picker.Item key={ap} value={ap}>
                  {({ selected }) => (
                    <span style={{
                      fontFamily: "'Raleway', sans-serif",
                      fontSize: selected ? fs.bodyLarge : fs.bodyInput,
                      fontWeight: selected ? 700 : 400,
                      color: selected ? d.accent : d.textSecondary,
                    }}>{ap}</span>
                  )}
                </Picker.Item>
              ))}
            </Picker.Column>
          </Picker>

          <div style={{
            display: 'flex', justifyContent: 'space-between',
            paddingTop: '8px', marginTop: '4px',
            borderTop: `1px solid ${d.border}`,
          }}>
            <button type="button" onClick={handleClear} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontFamily: "'Raleway', sans-serif", fontSize: fs.button, fontWeight: 700,
              letterSpacing: '1.5px', textTransform: 'uppercase',
              color: d.textTertiary, padding: '6px 10px',
            }}>Clear</button>
            <button type="button" onClick={handleConfirm} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontFamily: "'Raleway', sans-serif", fontSize: fs.button, fontWeight: 700,
              letterSpacing: '1.5px', textTransform: 'uppercase',
              color: d.accent, padding: '6px 10px',
            }}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
